/**
 * Instants — a real moment on the global timeline, rendered in the **viewer's**
 * timezone (unlike calendar dates and wall-clock times, which are
 * zone-independent and never shifted). On the wire an instant is either Unix
 * **seconds** (a number — timestamps, payment dates) or a **UTC**
 * `YYYY-MM-DD HH:MM:SS` string (accommodation check-in/out). HTML
 * `datetime-local` inputs are in the viewer's local zone.
 *
 * Display deliberately uses the runtime timezone — that *is* the viewer's zone —
 * and the device locale, so the 12/24-hour clock follows the browser setting
 * (issue #165). Flights are the exception: `datetime24` pins the 24-hour
 * aviation rendering regardless of locale (issue #164). The test suite pins
 * `TZ=UTC` and stubs `navigator.language`, so the assertions are deterministic.
 */

import { deviceLocale } from './locale'

export type InstantStyle =
	| 'datetime' // 'Jan 15, 2:30 PM'
	| 'datetime24' // 'Jan 15, 14:30' — flights only (aviation convention)
	| 'datetimeWithYear' // 'Jan 15, 2026, 2:30 PM'
	| 'datetimeWithSeconds' // 'Jan 15, 2026, 2:30:07 PM'
	| 'dateWeekday' // 'Thu, Jan 15, 2026'
	| 'dateMedium' // 'Jan 15, 2026'
	| 'dateLong' // 'January 15, 2026'
	| 'monthDay' // 'Jan 15'
	| 'time' // '2:30 PM'

// datetime24 uses hourCycle (not hour12: false) so midnight is "00:30", never "24:30".
const STYLE_OPTS: Record<InstantStyle, Intl.DateTimeFormatOptions> = {
	datetime: { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' },
	datetime24: { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' },
	datetimeWithYear: {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	},
	datetimeWithSeconds: {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		second: '2-digit',
	},
	dateWeekday: { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' },
	dateMedium: { year: 'numeric', month: 'short', day: 'numeric' },
	dateLong: { year: 'numeric', month: 'long', day: 'numeric' },
	monthDay: { month: 'short', day: 'numeric' },
	// timeStyle (not hour/minute components) so each locale keeps its preferred
	// pattern — de-DE pads midnight to "00:30", en-US reads "12:30 AM".
	time: { timeStyle: 'short' },
}

const pad = (n: number): string => String(n).padStart(2, '0')

/** Current time as Unix seconds. */
export const nowUnix = (): number => Math.floor(Date.now() / 1000)

/** Unix seconds → the JS `Date` for that instant. */
export const unixToDate = (unix: number): Date => new Date(unix * 1000)

/** Parse a UTC `YYYY-MM-DD HH:MM:SS` wire string into the instant it denotes. */
export const parseUtcInstant = (utcString: string): Date => new Date(utcString.replace(' ', 'T') + 'Z')

/**
 * Format a real instant in the viewer's timezone and locale. The flight style
 * (`datetime24`) is the exception: aviation convention pins its rendering, so
 * it ignores the device locale (issues #164/#165).
 */
export const formatInstant = (date: Date, style: InstantStyle = 'dateMedium'): string =>
	date.toLocaleString(style === 'datetime24' ? 'en-US' : deviceLocale(), STYLE_OPTS[style])

/** Format Unix seconds in the viewer's timezone; `null` for `null`/`undefined` input. */
export const formatUnix = (
	unix: number | null | undefined,
	style: InstantStyle = 'dateMedium',
): string | null => (unix == null ? null : formatInstant(unixToDate(unix), style))

/** Format a UTC wire string in the viewer's timezone. */
export const formatUtcInstant = (utcString: string, style: InstantStyle = 'dateMedium'): string =>
	formatInstant(parseUtcInstant(utcString), style)

/** A JS `Date` → datetime-local input `YYYY-MM-DDTHH:MM` (viewer-local fields). */
export const dateToInput = (date: Date): string =>
	`${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`

/**
 * Unix seconds → ISO calendar date `YYYY-MM-DD` in the viewer's zone — the day
 * the viewer sees the instant fall on, matching how the app renders it.
 * For machine-friendly artifacts (CSV exports); use `formatUnix` for prose.
 */
export const formatUnixIsoDate = (unix: number): string => {
	const date = unixToDate(unix)
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** datetime-local input (viewer-local) → UTC wire string `YYYY-MM-DD HH:MM:SS`. */
export const inputToUtcString = (localInput: string): string => {
	const date = new Date(localInput)
	return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`
}

/** UTC wire string → datetime-local input (viewer-local) for editing. */
export const utcStringToInput = (utcString: string): string => dateToInput(parseUtcInstant(utcString))
